import {Box, Button, Tooltip} from '@mui/material';
import {get_currency, get_price_str} from 'common';
import {request_close_order, request_grab_order} from 'connector/order';
import useSound, {useAlert} from 'hooks/generalHooks';
import {useMarketInfo} from 'hooks/marketHooks';
import {useActiveOrders} from 'hooks/orderHooks';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import {IOrder} from 'types';
import LoadingSpinner from './animation/LoadingSpinner';
import ExchangeLogo from './ExchangeLogo';

const SCALING_FACTOR = 0.001;

export default function TakeButton(props: {order: IOrder; is_processing: boolean; set_is_processing: (is_processing: boolean) => void}) {
	const {is_processing, set_is_processing, order} = props;
	const {order_id, state, volume, order_price, profit} = order;

	const {showAlert, hideAlert, alert} = useAlert();
	const [press_count, set_press_count] = useState(0);
	const {refresh_state, adjust_order} = useActiveOrders();
	const currency = get_currency(order.exchange);
	const last_state = useRef(state);
	const {market_info} = useMarketInfo(order.exchange, order.ticker);
	const min_total_cost = useMemo(() => (market_info ? parseFloat(market_info.market.bid.min_total) * SCALING_FACTOR : 0), [market_info]);
	const total_cost = useMemo(() => order_price * volume, [order_price, volume]);

	const sound_do = useSound('/sounds/232006__danmitch3ll__xylophone-f.mp3');
	const sound_mi = useSound('/sounds/232004__danmitch3ll__xylophone-a.mp3');
	const sound_sol = useSound('/sounds/232001__danmitch3ll__xylophone-c2.mp3');
	const sound_open = useSound('/sounds/noti.mp3');
	const sound_tada = useSound('/sounds/397355__plasterbrain__tada-fanfare-a.mp3');
	const sound_die = useSound('/sounds/637070__sergequadrado__surprised-child-voice-sound.mp3');

	const stop_all_sounds = useCallback(() => {
		sound_do.stop();
		sound_mi.stop();
		sound_sol.stop();
		sound_open.stop();
		sound_tada.stop();
		sound_die.stop();
	}, [sound_do, sound_mi, sound_sol, sound_open, sound_tada, sound_die]);

	useEffect(() => {
		if (last_state.current === 'opening' && state === 'open') {
			stop_all_sounds();
			sound_open.play();
		} else if (last_state.current === 'closing' && state === 'closed') {
			stop_all_sounds();
			if (profit < 0) sound_die.play();
			else sound_tada.play();
		}

		last_state.current = state;
	}, [state]);

	const handleGrab = () => {
		if (is_processing || state !== 'open_watch') return;

		if (press_count === 0 || !alert.open) {
			sound_do.play();
			flushSync(() => set_press_count(1));
			showAlert('Press 2 times more', 'info');
		} else if (press_count === 1) {
			sound_mi.play();
			flushSync(() => set_press_count(2));
			showAlert('Press 1 time more', 'info');
		} else if (press_count === 2) {
			sound_sol.play();
			hideAlert();
			set_press_count(0);

			flushSync(() => set_is_processing(true));

			request_grab_order(order_id).then((resp: any) => {
				if (resp.code === 200) {
					adjust_order({...order, state: 'opening', order_price: resp.data.order_price});
					refresh_state(order_id).then(() => set_is_processing(false));
				}
			});
		}
	};

	const handleClose = () => {
		if (is_processing || state !== 'open') return;

		if (press_count === 0 || !alert.open) {
			sound_do.play();
			flushSync(() => set_press_count(1));
			showAlert('Press 2 times more', 'info');
			return;
		} else if (press_count === 1) {
			sound_mi.play();
			flushSync(() => set_press_count(2));
			showAlert('Press 1 time more', 'info');
			return;
		} else if (press_count === 2) {
			sound_sol.play();
			hideAlert();
			set_press_count(0);

			flushSync(() => set_is_processing(true));

			request_close_order(order_id).then((resp: any) => {
				adjust_order({...order, state: 'closing', close_price: resp.data.close_price});
				refresh_state(order_id).then(() => set_is_processing(false));
			});
		}
	};

	return (
		<Box minWidth="100px" height="100%" display="flex" gap="5px" alignItems="center" justifyContent="center" position="relative">
			<Box
				position="absolute"
				left={0}
				top={0}
				right={0}
				bottom={0}
				display="flex"
				justifyContent="center"
				alignItems="center"
				sx={{filter: 'opacity(0.2)', pointerEvents: 'none'}}
			>
				<ExchangeLogo exchange={order.exchange} />
			</Box>

			{state === 'open_watch' ? (
				<Box width="100%">
					<Button id="grab-button" variant="outlined" size="small" fullWidth onClick={handleGrab} disabled={is_processing || state !== 'open_watch'}>
						{'Grab!'}
					</Button>
				</Box>
			) : (
				<Tooltip title={get_price_str(order_price * volume, true)} arrow placement="top">
					<Box width="100%">
						<Button
							id="close-button"
							variant="outlined"
							color={profit && profit < 0 ? 'error' : 'primary'}
							size="small"
							fullWidth
							onClick={handleClose}
							disabled={is_processing || state !== 'open' || total_cost < min_total_cost}
						>
							{state === 'pending' || state === 'opening' || state === 'closing' ? (
								<LoadingSpinner size={20} />
							) : state === 'canceled' ? (
								'Canceled'
							) : (
								`${get_price_str(profit, false)} ${currency}`
							)}
						</Button>
					</Box>
				</Tooltip>
			)}
		</Box>
	);
}
